import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { isEmpty } from 'lodash';

import { verify } from 'site-modules/shared/components/profile/firebase-auth';
import { formValidation } from 'site-modules/shared/components/form-validation/form-validation';
import { firebaseValidation } from 'site-modules/shared/components/profile/firebase-validation';

import { trackEmailOnlySignUpSuccess } from 'client/engagement-handlers/profile-engagement-handler/profile-engagement-handler';
import { Link } from 'site-modules/shared/components/link/link';
import { FormField } from 'site-modules/shared/components/form-field/form-field';
import { ProfileFormErrors } from 'site-modules/shared/components/profile/profile-form-errors/profile-form-errors';
import { SMART_SIGN_SCREEN, validators } from 'site-modules/shared/components/profile/smart-sign/helper';
import { ReCaptchaButtonAsync } from 'site-modules/shared/components/profile/recaptcha-button/recaptcha-button-async';

function SignEmailFormUI({
  validate,
  resetValidation,
  validateEmailAsync,
  validationErrors,
  fieldRef,
  fieldsOrder,
  fields,
  firebaseError,
  parseFirebaseError,
  isVerifyEmail,
  profileDataEmail,
  successCallback,
  ctaText,
  isAuthenticated,
  isSignInAction,
  disableRecaptcha,
}) {
  const [isSpinnerActive, setSpinnerActive] = useState(false);

  function handleSubmitForm(event) {
    event.preventDefault();
  }

  async function handleSubmit(token) {
    const nextScreen = isVerifyEmail || isSignInAction ? SMART_SIGN_SCREEN.SIGN_IN : SMART_SIGN_SCREEN.SIGN_UP;

    setSpinnerActive(true);
    const email = fields.email.value();
    return verify({ email, token, disableRecaptcha })
      .then(async () => {
        const emailValidation = validate() && (await validateEmailAsync());
        if (emailValidation && emailValidation.isEmailValid) {
          const emailValue = fields.email.value();
          if (isAuthenticated && !profileDataEmail && nextScreen === SMART_SIGN_SCREEN.SIGN_UP) {
            trackEmailOnlySignUpSuccess();
          }

          successCallback({
            emailValue,
            nextScreen,
          });
        }
      })
      .catch(error => {
        parseFirebaseError(error);
      });
  }

  useEffect(() => {
    if (!isEmpty(firebaseError) || !isEmpty(validationErrors)) {
      setSpinnerActive(false);
    }
  }, [firebaseError, validationErrors]);

  const isValidEmail = !('email' in validationErrors);

  return (
    <div className="smart-sign-step-one">
      <form id="smart-sign-email-form" noValidate onSubmit={handleSubmitForm}>
        <FormField
          id="sign-up-email"
          className="mb-0"
          labelClassName="medium mb-0_25"
          inputClassName="inspectletIgnore"
          name="email"
          ref={field => fieldRef('email', field)}
          label="Email *"
          type="email"
          autoComplete="email"
          isValid={isValidEmail}
          onBlur={validate}
          onChange={resetValidation}
          data-tracking-id="provide_email"
          data-tracking-ignore-value
          data-no-refresh
          aria-required
          aria-invalid={!isValidEmail}
          aria-describedby="sign-up-error profile-form-errors"
          defaultValue={profileDataEmail}
        />
        {!isVerifyEmail && (
          <div className="mt-0_25 text-gray-darker size-10">
            By clicking {ctaText}, I accept the terms of the{' '}
            <Link
              target="_blank"
              rel="noopener noreferrer"
              to="/about/visitor-agreement.html"
              className="text-gray-darker disclaimer"
            >
              <u>Edmunds Visitor Agreement</u>
            </Link>
            .{' '}
            <Link
              target="_blank"
              rel="noopener noreferrer"
              to="/about/privacy.html"
              className="text-gray-darker disclaimer"
            >
              <u>Our collection and use of your personal information</u>
            </Link>
            .
          </div>
        )}
        <ProfileFormErrors
          fieldsOrder={fieldsOrder}
          validationErrors={validationErrors}
          firebaseError={firebaseError}
          isAuthenticatedSaveModal
          className="xsmall"
        />
        <div className="px-2_5 px-sm-3_5 mt-0_75">
          <ReCaptchaButtonAsync
            action={isSignInAction ? 'SIGN_IN' : 'SIGN_UP'}
            id="smart-sign-email-submit"
            isActionProgress={isSpinnerActive}
            handleAction={handleSubmit}
            color="blue-50"
            className={classnames('w-100', { 'spinner-active': isSpinnerActive })}
            btnText={ctaText}
            btnActionText={ctaText}
          />
        </div>
      </form>
    </div>
  );
}

SignEmailFormUI.propTypes = {
  successCallback: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  validateEmailAsync: PropTypes.func.isRequired,
  validate: PropTypes.func.isRequired,
  resetValidation: PropTypes.func.isRequired,
  validationErrors: PropTypes.objectOf(PropTypes.string),
  fieldRef: PropTypes.func.isRequired,
  fields: PropTypes.objectOf(
    PropTypes.shape({
      email: PropTypes.object,
    })
  ).isRequired,
  fieldsOrder: PropTypes.arrayOf(PropTypes.string).isRequired,
  firebaseError: PropTypes.shape({}),
  parseFirebaseError: PropTypes.func.isRequired,
  profileDataEmail: PropTypes.string,
  ctaText: PropTypes.string,
  isVerifyEmail: PropTypes.bool.isRequired,
  isSignInAction: PropTypes.bool,
  disableRecaptcha: PropTypes.bool,
};

SignEmailFormUI.defaultProps = {
  validationErrors: {},
  firebaseError: null,
  profileDataEmail: null,
  ctaText: 'Continue',
  emailFormRequired: false,
  isSignInAction: false,
  disableRecaptcha: false,
};

export const SignEmailForm = formValidation(firebaseValidation(SignEmailFormUI), validators);
